import { AssetCard } from './AssetCard';
import { useAssetData } from '@/hooks/useAssetData';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAssetStore } from '@/stores/assetStore';
import { Asset } from '@/stores/assetStore';
import { useState, memo, useCallback, useMemo } from 'react';
import { aktoolsService } from '@/services/aktools';
import { AktoolsHistoryPage } from './AktoolsHistoryPage';

interface SortableAssetItemProps {
  asset: Asset;
  onOpenHistory?: (asset: Asset) => void;
}

const SortableAssetItem = memo(
  ({ asset, onOpenHistory }: SortableAssetItemProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: asset.id });

    const style = useMemo(
      () => ({
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
        opacity: isDragging ? 0.5 : 1,
      }),
      [transform, transition, isDragging]
    );

    const isAktoolsAsset = useMemo(() => {
      const sourceUrl = asset.sourceUrl;
      if (!sourceUrl) return false;
      try {
        const baseUrl = aktoolsService.getBaseUrl();
        if (!baseUrl || baseUrl.trim() === '') return false;
        if (baseUrl.startsWith('/')) {
          return sourceUrl.startsWith(baseUrl);
        }
        if (sourceUrl.startsWith(baseUrl)) return true;
        try {
          const baseUrlObj = new URL(baseUrl);
          const sourceUrlObj = new URL(sourceUrl);
          return (
            baseUrlObj.hostname === sourceUrlObj.hostname &&
            baseUrlObj.port === sourceUrlObj.port
          );
        } catch {
          return false;
        }
      } catch {
        return false;
      }
    }, [asset.sourceUrl]);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`relative ${isDragging ? 'z-50' : 'z-auto'}`}
      >
        <div className="relative">
          <AssetCard asset={asset} />
          {/* 拖动手柄 - 右侧图标提示（macOS 风格三条横线） */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
            {isAktoolsAsset && onOpenHistory && (
              <button
                type="button"
                onClick={() => onOpenHistory(asset)}
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
                title="历史曲线"
                aria-label="历史曲线"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 15l4-4 3 3 6-6"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M18 8h1v1"
                  />
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="3"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            )}
            <div
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors cursor-grab active:cursor-grabbing"
              title="拖动排序"
              aria-label="拖动排序"
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="1" y1="3" x2="13" y2="3" />
                <line x1="1" y1="8" x2="13" y2="8" />
                <line x1="1" y1="13" x2="13" y2="13" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export const AssetList = () => {
  const { assets } = useAssetData();
  const { reorderAssets } = useAssetStore();
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [historyAsset, setHistoryAsset] = useState<Asset | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = assets.findIndex(asset => asset.id === active.id);
        const newIndex = assets.findIndex(asset => asset.id === over.id);

        const newOrder = arrayMove(assets, oldIndex, newIndex);
        reorderAssets(newOrder);
      }
    },
    [assets, reorderAssets]
  );

  const activeAsset = useMemo(
    () => (activeId ? assets.find(asset => asset.id === activeId) : null),
    [activeId, assets]
  );

  const assetIds = useMemo(() => assets.map(asset => asset.id), [assets]);

  if (historyAsset) {
    return (
      <AktoolsHistoryPage
        asset={historyAsset}
        onBack={() => setHistoryAsset(null)}
      />
    );
  }

  return (
    <div className="w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-3">
          <SortableContext
            items={assetIds}
            strategy={verticalListSortingStrategy}
          >
            {assets.map(asset => (
              <SortableAssetItem
                key={asset.id}
                asset={asset}
                onOpenHistory={setHistoryAsset}
              />
            ))}
          </SortableContext>
          {assets.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              {t('assets.add')}
            </div>
          )}
        </div>
        <DragOverlay>
          {activeAsset ? (
            <div className="opacity-90 rotate-2 shadow-2xl">
              <AssetCard asset={activeAsset} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
